<template>
  <teleport to="body">
    <transition name="cookie-fade">
      <div v-if="isVisible" class="cookie-layer">
        <div class="cookie-overlay"></div>

        <div class="cookie-banner">
          <div class="cookie-content">
            <div class="cookie-text">
              <h3>Мы используем cookies</h3>

              <p>
                Необходимые технологии помогают сайту работать.
                С вашего разрешения мы также можем использовать аналитику,
                чтобы улучшать Hr3.
              </p>

              <RouterLink to="/terms-of-use" class="cookie-link">
                Подробнее
              </RouterLink>
            </div>

            <div class="cookie-actions">
              <button
                class="cookie-button cookie-button-secondary"
                type="button"
                @click="acceptNecessary"
              >
                Только необходимые
              </button>

              <button
                class="cookie-button cookie-button-primary"
                type="button"
                @click="acceptAll"
              >
                Принять все
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { onMounted, ref } from "vue";

const isVisible = ref(false);

const CONSENT_KEY = "hr3_cookie_consent";

onMounted(() => {
  const savedConsent = localStorage.getItem(CONSENT_KEY);

  if (!savedConsent) {
    isVisible.value = true;
  }
});

const saveConsent = (analyticsAllowed) => {
  const consent = {
    necessary: true,
    analytics: analyticsAllowed,
    updatedAt: new Date().toISOString()
  };

  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  isVisible.value = false;
};

const acceptNecessary = () => {
  saveConsent(false);
};

const acceptAll = () => {
  saveConsent(true);

  // Аналитику запускаем только после согласия.
  // initializeAnalytics();
};
</script>

<style scoped>
.cookie-layer {
  position: fixed;
  inset: 0;
  z-index: 2147483000;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  padding: 20px;

  box-sizing: border-box;
}

.cookie-overlay {
  position: absolute;
  inset: 0;

  background:
    radial-gradient(
      circle at center,
      rgba(0, 0, 0, 0.18),
      rgba(0, 0, 0, 0.58)
    );

  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.cookie-banner {
  position: relative;
  z-index: 1;

  width: min(760px, 100%);
}

.cookie-content {
  width: 100%;
  padding: 20px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  box-sizing: border-box;

  background: rgba(32, 34, 38, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;

  box-shadow:
    0 18px 60px rgba(0, 0, 0, 0.42),
    0 4px 16px rgba(0, 0, 0, 0.24);

  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.cookie-text {
  flex: 1;
  min-width: 0;
}

.cookie-text h3 {
  margin: 0 0 8px;

  color: #ffffff;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  line-height: 1.25;
}

.cookie-text p {
  margin: 0;

  color: rgba(255, 255, 255, 0.72);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.5;
}

.cookie-link {
  display: inline-block;
  margin-top: 9px;

  color: #4b9cff;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  text-decoration: none;

  transition: opacity 0.2s ease;
}

.cookie-link:hover {
  opacity: 0.8;
}

.cookie-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.cookie-button {
  min-height: 42px;
  padding: 0 18px;

  border: none;
  border-radius: 21px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;

  cursor: pointer;

  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    background-color 0.2s ease;
}

.cookie-button:hover {
  transform: translateY(-1px);
}

.cookie-button:active {
  transform: translateY(0);
}

.cookie-button-primary {
  color: #ffffff;
  background: linear-gradient(to right, #2173e5, #204f90);
}

.cookie-button-primary:hover {
  opacity: 0.92;
}

.cookie-button-secondary {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
}

.cookie-button-secondary:hover {
  background: rgba(255, 255, 255, 0.16);
}

.cookie-fade-enter-active,
.cookie-fade-leave-active {
  transition: opacity 0.25s ease;
}

.cookie-fade-enter-active .cookie-banner,
.cookie-fade-leave-active .cookie-banner {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.cookie-fade-enter-from,
.cookie-fade-leave-to {
  opacity: 0;
}

.cookie-fade-enter-from .cookie-banner,
.cookie-fade-leave-to .cookie-banner {
  opacity: 0;
  transform: translateY(18px) scale(0.98);
}

[data-theme="light"] .cookie-content {
  background: rgba(255, 255, 255, 0.96);
  border-color: rgba(0, 0, 0, 0.08);

  box-shadow:
    0 18px 60px rgba(0, 0, 0, 0.24),
    0 4px 16px rgba(0, 0, 0, 0.1);
}

[data-theme="light"] .cookie-text h3 {
  color: #111111;
}

[data-theme="light"] .cookie-text p {
  color: rgba(0, 0, 0, 0.65);
}

[data-theme="light"] .cookie-button-secondary {
  color: #111111;
  background: rgba(0, 0, 0, 0.07);
}

[data-theme="light"] .cookie-button-secondary:hover {
  background: rgba(0, 0, 0, 0.11);
}

@media (max-width: 640px) {
  .cookie-layer {
    padding:
      12px
      12px
      calc(12px + env(safe-area-inset-bottom));
  }

  .cookie-content {
    align-items: stretch;
    flex-direction: column;
    gap: 16px;

    padding: 18px;
    border-radius: 18px;
  }

  .cookie-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .cookie-button {
    width: 100%;
    padding: 0 12px;
  }
}

@media (max-width: 430px) {
  .cookie-actions {
    grid-template-columns: 1fr;
  }

  .cookie-text h3 {
    font-size: 15px;
  }

  .cookie-text p {
    font-size: 12px;
  }
}
</style>